import {
  ROLLING_DATE_ANCHORS,
  isRollingDateToken,
  type RollingDateToken,
} from './rollingDates';

/**
 * Every BARE `@`-token the platform recognises in a filter value.
 *
 * Two families live here:
 *  - **Identity/context tokens** (`@me`, `@root`, `@parent`, `@center`) —
 *    resolved to a sphere id against the request's execution context.
 *  - **Rolling-date anchors** (`@today` and the `@startOf*` / `@endOf*`
 *    family) — resolved to a calendar date against the viewer's time zone.
 *    Each anchor also accepts an offset suffix (`@today-30d`,
 *    `@startOfMonth-1m`); those are NOT enumerable, so they are recognised by
 *    `isFilterVariable` via the grammar rather than by membership here. See
 *    `rollingDates.ts` for the grammar and the calendar arithmetic.
 *
 * `@today` is listed once — it is both the historical context token and the
 * identity anchor of the rolling-date family.
 */
export const CONTEXT_FILTER_VARIABLES = [
  '@me',
  '@root',
  '@parent',
  '@center',
  ...ROLLING_DATE_ANCHORS,
] as const;

export type ContextFilterVariable = (typeof CONTEXT_FILTER_VARIABLES)[number] | RollingDateToken;

/**
 * True when `value` is a recognised filter-variable token — a bare token from
 * `CONTEXT_FILTER_VARIABLES`, or a rolling-date anchor carrying a valid offset
 * suffix.
 *
 * An `@`-prefixed string the grammar does NOT accept returns `false`, and every
 * consumer must keep treating that as "a literal, not a token". Widening this
 * to "anything starting with `@`" would make a typo like `@todya-7d` resolve to
 * something, which is exactly the silent filter corruption the resolver's
 * "Unknown @-token left as literal" warn path exists to surface.
 */
export function isFilterVariable(value: unknown): value is ContextFilterVariable {
  if (typeof value !== 'string') return false;
  if ((CONTEXT_FILTER_VARIABLES as readonly string[]).includes(value)) return true;
  return isRollingDateToken(value);
}
